import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelfServiceLanguage } from '../../context/SelfServiceLanguageContext';
import { deviceCashierService, deviceRegisterService } from '../../services/posApi';
import { useProductsSync } from '../../hooks/useProductsSync';
import SelfServiceFooter from '../../components/SelfServiceFooter';
import './SelfService.css';

const SelfServiceAdminMenu = () => {
    const navigate = useNavigate();
    const { t } = useSelfServiceLanguage();
    const { syncProducts, isSyncing: isProductsSyncing, productsCount, lastSyncTime } = useProductsSync();
    const [isCashierSyncing, setIsCashierSyncing] = useState(false);
    const [isRegisterSyncing, setIsRegisterSyncing] = useState(false);
    const [currentCashier, setCurrentCashier] = useState(null);
    const [registerInfo, setRegisterInfo] = useState(null);

    useEffect(() => {
        checkAuthStatus();
    }, []);

    const checkAuthStatus = async () => {
        try {
            // Get cashier info
            const cashierData = localStorage.getItem('device_cashier_info');
            if (cashierData) {
                const parsedData = JSON.parse(cashierData);
                if (parsedData.cashier_authorized && parsedData.cashier) {
                    setCurrentCashier(parsedData.cashier);
                }
            }

            // Get register info
            const savedRegisterInfo = deviceRegisterService.getSavedRegisterInfo();
            if (savedRegisterInfo) {
                setRegisterInfo(savedRegisterInfo);
            }
        } catch (error) {
            console.error('Ошибка при проверке статуса авторизации:', error);
        }
    };

    const handleSalesHistory = () => {
        // Navigate to device sales list
        navigate('/device/sales');
    };

    const handleLogout = async () => {
        try {
            const cashierInfo = deviceCashierService.getSavedCashierInfo();
            if (cashierInfo?.cashier) {
                const generateUUID = () => {
                    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
                        const r = Math.random() * 16 | 0;
                        const v = c === 'x' ? r : (r & 0x3 | 0x8);
                        return v.toString(16);
                    });
                };

                await deviceCashierService.logoutCashier(
                    cashierInfo.cashier.uuid,
                    generateUUID(),
                    generateUUID()
                );
            }

            // Clear self-service cart
            localStorage.removeItem('self_service_cart');

            // Navigate to cashier login
            navigate('/device/cashier-login');
        } catch (error) {
            console.error('Logout error:', error);
            // Navigate anyway
            navigate('/device/cashier-login');
        }
    };

    const handleBack = () => {
        navigate('/device/self-service');
    };

    const handleProductsSync = async () => {
        try {
            console.log('Запуск синхронизации товаров...');
            await syncProducts(true);
            console.log('Синхронизация товаров завершена');
        } catch (error) {
            console.error('Ошибка синхронизации товаров:', error);
        }
    };

    const handleCashierSync = async () => {
        if (!currentCashier) return;

        try {
            setIsCashierSyncing(true);
            console.log('Запуск синхронизации кассира...');

            const cashierStatus = await deviceCashierService.getCashierStatus();
            localStorage.setItem('device_cashier_info', JSON.stringify(cashierStatus));

            // Update state
            checkAuthStatus();

            console.log('Синхронизация кассира завершена');
        } catch (error) {
            console.error('Ошибка синхронизации кассира:', error);
        } finally {
            setIsCashierSyncing(false);
        }
    };

    const handleRegisterSync = async () => {
        try {
            setIsRegisterSyncing(true);
            console.log('Запуск синхронизации кассы...');

            const registerInfo = await deviceRegisterService.getRegisterInfo();
            localStorage.setItem('device_register_info', JSON.stringify(registerInfo));

            // Update state
            setRegisterInfo(registerInfo);

            console.log('Синхронизация кассы завершена');
        } catch (error) {
            console.error('Ошибка синхронизации кассы:', error);
        } finally {
            setIsRegisterSyncing(false);
        }
    };

    const formatLastSync = () => {
        if (!lastSyncTime) return 'Никогда';

        const date = new Date(lastSyncTime);
        const now = new Date();
        const diffMinutes = Math.floor((now - date) / 60000);

        if (diffMinutes < 1) return 'Только что';
        if (diffMinutes < 60) return `${diffMinutes} мин. назад`;
        if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)} ч. назад`;
        return date.toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className="self-service-container">
            {/* Content */}
            <div className="self-service-content" style={{ padding: '65px' }}>
                <h1 style={{
                    fontSize: 60,
                    fontWeight: 700,
                    margin: 0,
                    marginBottom: 48,
                    fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                    color: '#17181A'
                }}>
                    Меню администратора
                </h1>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 24, width: '100%' }}>
                    {/* Sync products button */}
                    <button
                        onClick={handleProductsSync}
                        disabled={isProductsSyncing}
                        style={{
                            width: '100%',
                            height: '124px',
                            background: isProductsSyncing ? '#95C9B8' : '#028A62',
                            border: 'none',
                            borderRadius: '50px',
                            fontSize: '38px',
                            fontWeight: 700,
                            fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                            color: 'white',
                            cursor: isProductsSyncing ? 'not-allowed' : 'pointer',
                            transition: 'all 0.3s',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            opacity: isProductsSyncing ? 0.7 : 1
                        }}
                    >
                        <span>{isProductsSyncing ? 'Синхронизация...' : 'Синхронизировать товары'}</span>
                        {!isProductsSyncing && (
                            <span style={{ fontSize: '20px', fontWeight: 500, opacity: 0.8 }}>
                                {productsCount ? `${productsCount} товаров` : 'Не синхронизировано'} • {formatLastSync()}
                            </span>
                        )}
                    </button>

                    {/* Sync cashier button */}
                    <button
                        onClick={handleCashierSync}
                        disabled={isCashierSyncing || !currentCashier}
                        style={{
                            width: '100%',
                            height: '124px',
                            background: isCashierSyncing ? '#95C9B8' : '#028A62',
                            border: 'none',
                            borderRadius: '50px',
                            fontSize: '38px',
                            fontWeight: 700,
                            fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                            color: 'white',
                            cursor: (isCashierSyncing || !currentCashier) ? 'not-allowed' : 'pointer',
                            transition: 'all 0.3s',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            opacity: (isCashierSyncing || !currentCashier) ? 0.7 : 1
                        }}
                    >
                        <span>{isCashierSyncing ? 'Синхронизация...' : 'Синхронизировать кассира'}</span>
                        {!isCashierSyncing && currentCashier && (
                            <span style={{ fontSize: '20px', fontWeight: 500, opacity: 0.8 }}>
                                {currentCashier.f_name} {currentCashier.l_name}
                            </span>
                        )}
                    </button>

                    {/* Sync register button */}
                    <button
                        onClick={handleRegisterSync}
                        disabled={isRegisterSyncing}
                        style={{
                            width: '100%',
                            height: '124px',
                            background: isRegisterSyncing ? '#95C9B8' : '#028A62',
                            border: 'none',
                            borderRadius: '50px',
                            fontSize: '38px',
                            fontWeight: 700,
                            fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                            color: 'white',
                            cursor: isRegisterSyncing ? 'not-allowed' : 'pointer',
                            transition: 'all 0.3s',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            opacity: isRegisterSyncing ? 0.7 : 1
                        }}
                    >
                        <span>{isRegisterSyncing ? 'Синхронизация...' : 'Синхронизировать кассу'}</span>
                        {!isRegisterSyncing && registerInfo && (
                            <span style={{ fontSize: '20px', fontWeight: 500, opacity: 0.8 }}>
                                {registerInfo.cash_register?.name || 'Касса'}
                            </span>
                        )}
                    </button>

                    <button
                        onClick={handleSalesHistory}
                        style={{
                            width: '100%',
                            height: '124px',
                            background: '#028A62',
                            border: 'none',
                            borderRadius: '50px',
                            fontSize: '38px',
                            fontWeight: 700,
                            fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                            color: 'white',
                            cursor: 'pointer',
                            transition: 'all 0.3s'
                        }}
                    >
                        История продаж
                    </button>

                    <button
                        onClick={handleLogout}
                        style={{
                            width: '100%',
                            height: '124px',
                            background: '#FF4D4F',
                            border: 'none',
                            borderRadius: '50px',
                            fontSize: '38px',
                            fontWeight: 700,
                            fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                            color: 'white',
                            cursor: 'pointer',
                            transition: 'all 0.3s'
                        }}
                    >
                        Выйти
                    </button>

                    <button
                        onClick={handleBack}
                        style={{
                            width: '100%',
                            height: '124px',
                            background: 'transparent',
                            border: '2px solid #028A62',
                            borderRadius: '50px',
                            fontSize: '38px',
                            fontWeight: 700,
                            fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                            color: '#028A62',
                            cursor: 'pointer',
                            transition: 'all 0.3s'
                        }}
                    >
                        Назад
                    </button>
                </div>
            </div>

            {/* Footer */}
            <SelfServiceFooter />
        </div>
    );
};

export default SelfServiceAdminMenu;
